import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { TOKEN_TYPE, getTokenFromCookies, setTokenToCookies, setTokenExpiry, clearAllTokens } from './cookies'
import { getRootDomain } from './domain'
import { redirectToSSOLogin } from './sso'
import { SSO_API_BASE_URL, AUTH_ENDPOINTS } from '@/features/auth/api/endpoints'

const SSO_REFRESH_URL = `${SSO_API_BASE_URL}${AUTH_ENDPOINTS.refresh}`

function getCookieDomain(): string | undefined {
  const root = getRootDomain(window.location.hostname)
  return root ? `.${root}` : undefined
}

// In-flight refresh promise — đảm bảo nhiều requests 401 đồng thời chỉ trigger 1 refresh
let refreshPromise: Promise<string | null> | null = null

async function performRefresh(): Promise<string | null> {
  const refreshToken = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN)
  if (!refreshToken) return null

  try {
    const res = await axios.post(SSO_REFRESH_URL, { refresh_token: refreshToken }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 10000,
    })
    const data = res.data
    if (!data?.success || !data?.data?.access_token) return null

    const { access_token, refresh_token, expires_in } = data.data
    const domain = getCookieDomain()
    setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, access_token, expires_in, domain)
    setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, refresh_token, 7 * 24 * 60 * 60, domain)
    setTokenExpiry(new Date(Date.now() + expires_in * 1000).toISOString(), domain)
    return access_token
  } catch {
    return null
  }
}

function getRefreshPromise(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/**
 * Cài interceptor chung cho mọi axios instance:
 * - Request: gắn Bearer token
 * - Response 401: thử refresh 1 lần (queue tránh refresh đồng thời), retry request
 * - Refresh thất bại: clear tokens + redirect SSO
 */
export function setupAuthInterceptor(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN)
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
      const status = error.response?.status

      // Skip nếu không phải 401 hoặc đã retry rồi
      if (status !== 401 || !originalRequest || originalRequest._retry) {
        return error.response || Promise.reject(error)
      }

      // Skip nếu chính request là refresh (tránh recursive)
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearAllTokens(getCookieDomain())
        return error.response || Promise.reject(error)
      }

      originalRequest._retry = true
      const newToken = await getRefreshPromise()

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return instance(originalRequest)
      }

      // Refresh failed → clear all + redirect SSO
      clearAllTokens(getCookieDomain())
      redirectToSSOLogin()
      return error.response || Promise.reject(error)
    },
  )
}
